import type { FilterOption } from "../FilterMultiSelect";

export type MentionKind = "person" | "process";

export type MentionOption = FilterOption & { selectAll?: boolean };
export type MentionCandidate = MentionOption & { kind: MentionKind };

/** An in-progress `@…` token: where it starts and what has been typed after it. */
export type ActiveMention = { start: number; query: string };

/**
 * A mention only starts at a word boundary, so an email address or a handle
 * pasted mid-word is left alone. The query stops at whitespace: process names
 * with spaces are matched by their first word and then narrowed by the list.
 */
const MENTION = /(^|\s)@([^\s@]*)$/;

export const findActiveMention = (text: string, caret: number): ActiveMention | null => {
  const match = MENTION.exec(text.slice(0, caret));
  if (!match) return null;
  const query = match[2];
  return { start: caret - query.length - 1, query };
};

export type MentionGroup = { kind: MentionKind; options: MentionCandidate[] };

/**
 * People first, then processes; both filtered by the query and already-chosen
 * ids. The two kinds stay in separate groups so the list can head them, and each
 * is capped on its own — a query matching many people should not push every
 * process out of sight.
 */
export const mentionGroups = (
  query: string,
  people: MentionOption[],
  processes: MentionOption[],
  chosenUserIds: string[],
  chosenProcessSlugs: string[],
  limitPerGroup = 5,
): MentionGroup[] => {
  const needle = query.trim().toLowerCase();
  const matches = (option: FilterOption) => option.label.toLowerCase().includes(needle);

  const group = (kind: MentionKind, options: MentionOption[], chosen: string[]): MentionGroup => ({
    kind,
    options: options
      .filter(option => !chosen.includes(option.id) && matches(option))
      .slice(0, limitPerGroup)
      .map((option): MentionCandidate => ({ ...option, kind })),
  });

  return [
    group("person", people, chosenUserIds),
    group("process", processes, chosenProcessSlugs),
  ].filter(entry => entry.options.length > 0);
};

/**
 * Expands an in-progress token to the candidate's readable name. Both kinds
 * stay plain text so the composer never shows raw Markdown; process mentions
 * become links on submit, in `linkProcessMentions`.
 */
export const textWithMention = (
  text: string,
  mention: ActiveMention,
  candidate: MentionCandidate,
): { text: string; caret: number } => {
  const replacement = `@${candidate.label}`;
  return {
    text: `${text.slice(0, mention.start)}${replacement}${text.slice(mention.start + 1 + mention.query.length)}`,
    caret: mention.start + replacement.length,
  };
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Turns plain `@Process Name` mentions into internal Markdown links as the
 * prompt leaves the composer, so the transcript shows a clickable process
 * while the composer stays readable. Only processes still in the scope are
 * linked: a mention whose chip was removed stays plain text. Longer names go
 * first so one that extends another is never half-linked.
 */
export const linkProcessMentions = (text: string, processes: FilterOption[]): string =>
  [...processes]
    .sort((a, b) => b.label.length - a.label.length)
    .reduce(
      (linked, process) =>
        linked.replace(
          new RegExp(`(^|\\s)@${escapeRegExp(process.label)}(?![\\w-])`, "g"),
          (_, boundary) =>
            `${boundary}[@${process.label}](/processes/${encodeURIComponent(process.id)}/)`,
        ),
      text,
    );
